import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Form,
    Badge,
    Spinner,
    InputGroup
} from "react-bootstrap";
import {
    FaFileExcel,
    FaSearch,
    FaCalendarAlt,
    FaClock,
    FaUserCheck,
    FaSignature
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    getDoc,
    doc,
    setDoc,
    updateDoc,
    serverTimestamp
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";
import moment from "moment";
import Swal from "sweetalert2";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import {
    requestApprovalWithReasonModal,
    showApprovalRequestCreatedAlert,
} from "../../functions/approvalFlowAlerts";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "us-central1");
const CONTROL_PAPELETAS_PROCESS = "control_papeletas";
const CONTROL_PAPELETAS_POLICY_KEYS = ["control_papeletas", "controlPapeletas"];

const TIPO_LABEL = "Descanso por Régimen";
const NORMALIZED_PAPELETA_TYPES = new Set(["descansoregimen"]);

const normalizarTexto = (valor) =>
    String(valor || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "");

const esPapeletaDescanso = (tipo) => {
    const normalizado = normalizarTexto(tipo);
    return NORMALIZED_PAPELETA_TYPES.has(normalizado);
};

const normalizarEstadoSolicitud = (estado) => {
    const raw = String(estado || "").trim().toLowerCase();
    if (!raw) return "";
    if (["aprobado", "aprobada", "approved"].includes(raw)) return "aprobado";
    if (["pendiente", "pending"].includes(raw)) return "pendiente";
    if (["rechazado", "rechazada", "denegado", "denegada", "denied"].includes(raw)) return "rechazado";
    if (["anulado", "anulada", "cancelado", "cancelada", "cancelled", "canceled"].includes(raw)) return "anulado";
    return raw;
};

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === "function") return value.toDate();
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const enumerateDateKeys = (startValue, endValue) => {
    const start = toDate(startValue);
    const end = toDate(endValue || startValue);
    if (!start || !end) return [];
    const minDate = start <= end ? start : end;
    const maxDate = start <= end ? end : start;
    const days = differenceInCalendarDays(maxDate, minDate);
    const keys = [];
    for (let i = 0; i <= days; i += 1) {
        keys.push(format(addDays(minDate, i), "yyyy-MM-dd"));
    }
    return keys;
};

function ControlPapeletas({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [solicitudes, setSolicitudes] = useState([]);
    const [filtro, setFiltro] = useState("");
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    const [estadoSolicitud, setEstadoSolicitud] = useState("pendiente");
    const [approvalRequests, setApprovalRequests] = useState([]);
    const [processingRequestId, setProcessingRequestId] = useState("");
    const [activeProcessType, setActiveProcessType] = useState(CONTROL_PAPELETAS_PROCESS);
    const [policyLevels, setPolicyLevels] = useState([]);
    const [policyLoaded, setPolicyLoaded] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        cargarDatos();
        cargarSolicitudesAprobacion();
        cargarPoliticaAprobacion();
    }, [usuario]);

    useEffect(() => {
        cargarSolicitudesAprobacion();
    }, [activeProcessType]); // eslint-disable-line

    const rolActual = String(usuario?.rol || "").trim().toLowerCase();
    const isSingleLevelFlow = Array.isArray(policyLevels) && policyLevels.length === 1;
    const singleLevelRoles = isSingleLevelFlow
        ? (Array.isArray(policyLevels[0]?.roles) ? policyLevels[0].roles : []).map((r) =>
              String(r || "").trim().toLowerCase()
          )
        : [];
    const canCurrentUserActAsSingleApprover =
        isSingleLevelFlow && singleLevelRoles.includes(rolActual);

    const cargarSolicitudesAprobacion = async () => {
        try {
            const tenantId = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
            if (!tenantId) return;
            const q = query(
                collection(db, `governance/${tenantId}/approvalRequests`),
                where("processType", "==", activeProcessType)
            );
            const snap = await getDocs(q);
            const docs = [];
            snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
            setApprovalRequests(docs);
        } catch (error) {
            console.error("Error cargando aprobaciones de papeletas:", error);
            setApprovalRequests([]);
        }
    };

    const cargarPoliticaAprobacion = async () => {
        try {
            const tenantId = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
            if (!tenantId) return;
            let loaded = false;
            for (const key of CONTROL_PAPELETAS_POLICY_KEYS) {
                const snap = await getDoc(
                    doc(db, `governance/${tenantId}/approvalPolicies/${key}`)
                );
                if (!snap.exists()) continue;
                const data = snap.data() || {};
                const normalized = (Array.isArray(data.levels) ? data.levels : [])
                    .map((lvl, index) => ({
                        level: Number(lvl?.level || index + 1),
                        roles: Array.isArray(lvl?.roles)
                            ? lvl.roles.map((r) => String(r || "").trim()).filter(Boolean)
                            : [],
                        minApprovers: Number(lvl?.minApprovers || 1),
                    }))
                    .filter((lvl) => lvl.level > 0);
                if (normalized.length > 0) {
                    normalized.sort((a, b) => a.level - b.level);
                    setPolicyLevels(normalized);
                    setActiveProcessType(key);
                    loaded = true;
                    break;
                }
            }
            if (!loaded) {
                setPolicyLevels([]);
            }
            setPolicyLoaded(true);
        } catch (error) {
            console.error("Error cargando política de Control Papeletas:", error);
            setPolicyLevels([]);
            setPolicyLoaded(true);
        }
    };

    const getRequestBySolicitud = (solicitud) =>
        approvalRequests.find((r) => {
            const resourceId = String(r.resourceId || "");
            const byPath = resourceId && resourceId === String(solicitud?._path || "");
            const byId = resourceId && resourceId === String(solicitud?.id || "");
            return (byPath || byId) && /^pending_l\d+$/i.test(String(r.status || ""));
        });

    const getRequestLevels = (request) => {
        const rawLevels = Array.isArray(request?.levels) ? request.levels : [];
        const normalized = rawLevels
            .map((lvl, index) => {
                const level = Number(lvl?.level || index + 1);
                const roles = Array.isArray(lvl?.roles)
                    ? lvl.roles.map((role) => String(role || "").trim()).filter(Boolean)
                    : [];
                return { level, roles, role: roles[0] || `nivel_${level}` };
            })
            .filter((lvl) => Number.isFinite(lvl.level) && lvl.level > 0)
            .sort((a, b) => a.level - b.level);
        if (normalized.length > 0) return normalized;
        return [];
    };

    const toRoleLabel = (role) => {
        const value = String(role || "").trim().toLowerCase();
        if (!value) return "Rol";
        if (value === "rh") return "RH";
        if (value === "admin") return "Admin";
        return value.charAt(0).toUpperCase() + value.slice(1);
    };

    const getPendingLevelNumber = (request) => {
        const match = String(request?.status || "").match(/^pending_l(\d+)$/i);
        return match ? Number(match[1]) : null;
    };

    const mergeDocs = (buckets) => {
        const byPath = new Map();
        buckets.forEach((bucket) => {
            bucket.forEach((item) => {
                const key = item._path || item.id;
                if (!byPath.has(key)) {
                    byPath.set(key, item);
                }
            });
        });
        return Array.from(byPath.values());
    };

    const normalizarSolicitud = (item = {}) => {
        const estadoRaw = String(item.estado || "").trim().toLowerCase();
        const estadoNormalizado =
            normalizarEstadoSolicitud(estadoRaw) ||
            (item.aprobado === true ? "aprobado" : item.aprobado === false ? "pendiente" : "");
        return {
            ...item,
            empleadoDni: item.empleadoDni || item.dni || "",
            nombreEmpleado: item.nombreEmpleado || item.nombreCompleto || item.nombres || "",
            estado: estadoNormalizado,
        };
    };

    const sortSolicitudes = (list = []) =>
        [...list].sort((a, b) => {
            const dateA = a.fechaInicio?.seconds
                ? a.fechaInicio.seconds
                : new Date(a.fechaInicio).getTime() / 1000;
            const dateB = b.fechaInicio?.seconds
                ? b.fechaInicio.seconds
                : new Date(b.fechaInicio).getTime() / 1000;
            return dateB - dateA;
        });

    const upsertSolicitudLocal = (solicitudPath, patch = {}) => {
        setSolicitudes((prev) =>
            prev.map((item) => {
                if (String(item._path || "") !== String(solicitudPath || "")) return item;
                return normalizarSolicitud({ ...item, ...patch });
            })
        );
    };

    const getShiftLabelFromRolConfig = (config = {}, dateKey) => {
        if (!config?.fechaInicio || !dateKey) return "-";
        if (config.fechaCese && String(dateKey) >= String(config.fechaCese)) return "C";
        const excepcion = config?.excepciones?.[dateKey];
        if (excepcion) return String(excepcion).toUpperCase();

        const startDate = parseISO(String(config.fechaInicio));
        const targetDate = parseISO(String(dateKey));
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(targetDate.getTime())) return "-";

        const diasTrabajo = Number(config.diasTrabajo) || 24;
        const diasDescanso = Number(config.diasDescanso) || 14;
        const cycleLength = diasTrabajo + diasDescanso;
        if (cycleLength <= 0) return "-";
        let positionInCycle = differenceInCalendarDays(targetDate, startDate) % cycleLength;
        if (positionInCycle < 0) positionInCycle = (cycleLength + positionInCycle) % cycleLength;
        if (positionInCycle < diasTrabajo) return `T${positionInCycle + 1}`;
        return `D${positionInCycle - diasTrabajo + 1}`;
    };

    const applyPapeletaToRolTurnos = async (solicitud) => {
        const ruc = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
        const dni = String(solicitud?.empleadoDni || solicitud?.dni || "").trim();
        const papeletaId = String(solicitud?.id || "").trim();
        if (!ruc || !dni || !papeletaId) return;

        const dateKeys = enumerateDateKeys(solicitud?.fechaInicio, solicitud?.fechaFin);
        if (dateKeys.length === 0) return;

        const legacyRef = doc(db, "pl_rol_turnos", ruc, "rol", dni);
        const legacySnap = await getDoc(legacyRef);
        const legacyData = legacySnap.exists() ? (legacySnap.data() || {}) : {};

        const sedeId = String(legacyData?.sedeId || "general");
        const sedeRef = doc(db, "pl_rol_turnos", ruc, "sedes", sedeId, "rol", dni);
        const sedeSnap = await getDoc(sedeRef);
        const currentData = sedeSnap.exists()
            ? { ...sedeSnap.data(), sedeId }
            : { ...legacyData, sedeId };

        if (!currentData?.fechaInicio) return;

        const applied = currentData?.papeletasAplicadas || {};
        if (applied[papeletaId]) return;

        const excepciones = { ...(currentData?.excepciones || {}) };
        const papeletaStyleByDate = { ...(currentData?.papeletaStyleByDate || {}) };
        const papeletaDateRefs = { ...(currentData?.papeletaDateRefs || {}) };

        for (const dateKey of dateKeys) {
            const label = String(getShiftLabelFromRolConfig(currentData, dateKey)).toUpperCase();
            if (label.startsWith("T") || label.startsWith("N")) {
                excepciones[dateKey] = "D";
            } else if (label.startsWith("D")) {
                // Mantener descanso existente, solo marcar estilo.
            } else {
                continue;
            }
            papeletaStyleByDate[dateKey] = "PAPELETA_APROBADA";
            papeletaDateRefs[dateKey] = papeletaId;
        }

        const patch = {
            ...currentData,
            dni,
            rucEmpresa: ruc,
            sedeId,
            excepciones,
            papeletaStyleByDate,
            papeletaDateRefs,
            papeletasAplicadas: {
                ...applied,
                [papeletaId]: {
                    dateKeys,
                    appliedAt: new Date().toISOString(),
                    source: "control_papeletas",
                },
            },
            ultimaActualizacion: new Date().toISOString(),
            updated_at: serverTimestamp(),
        };

        await Promise.all([
            setDoc(sedeRef, patch, { merge: true }),
            setDoc(legacyRef, patch, { merge: true }),
        ]);
    };

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const startedAt = Date.now();
            const ruc = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
            if (!ruc) {
                console.warn("[ControlPapeletas] Usuario sin RUC definido", usuario);
                setSolicitudes([]);
                return;
            }
            const obtenerDocs = async (refBase, fieldName, withOrder = false) => {
                try {
                    const constraints = [where(fieldName, "==", ruc)];
                    const snap = await getDocs(query(refBase, ...constraints));
                    return snap.docs.map((docSnap) => ({
                        id: docSnap.id,
                        _path: docSnap.ref.path,
                        ...docSnap.data(),
                    }));
                } catch (e) {
                    console.warn(`[ControlPapeletas] Consulta fallida ${fieldName}${withOrder ? " + orderBy" : ""}:`, e?.message || e);
                    return [];
                }
            };

            const obtenerAusenciasPorUsuarios = async () => {
                const uniqueDnis = new Set();

                const usersByRucEmpresa = await obtenerDocs(collection(db, "users"), "rucEmpresa", false);
                const usersByRuc =
                    usersByRucEmpresa.length > 0
                        ? []
                        : await obtenerDocs(collection(db, "users"), "ruc", false);

                mergeDocs([usersByRucEmpresa, usersByRuc]).forEach((u) => {
                    const dni = String(u?.dni || "").trim();
                    if (dni) uniqueDnis.add(dni);
                });

                const dnis = Array.from(uniqueDnis);
                if (dnis.length === 0) return [];

                const resultados = [];
                const chunkSize = 50;

                for (let i = 0; i < dnis.length; i += chunkSize) {
                    const chunk = dnis.slice(i, i + chunkSize);
                    const snaps = await Promise.all(
                        chunk.map(async (dni) => {
                            try {
                                const refAusenciasDni = collection(db, `ausencias/${dni}/ausencias`);
                                const snap = await getDocs(refAusenciasDni);
                                return snap.docs.map((docSnap) => ({
                                    id: docSnap.id,
                                    _path: docSnap.ref.path,
                                    ...docSnap.data(),
                                }));
                            } catch (e) {
                                console.warn(`[ControlPapeletas] No se pudo leer ausencias de ${dni}:`, e?.message || e);
                                return [];
                            }
                        })
                    );
                    resultados.push(...snaps.flat());
                }

                return resultados;
            };

            const variantesAusencias = await obtenerAusenciasPorUsuarios();
            const todasSolicitudes = mergeDocs([variantesAusencias]);
            const data = todasSolicitudes
                .filter((item) => esPapeletaDescanso(item.tipo))
                .map((item) => normalizarSolicitud(item));

            console.info(
                `[ControlPapeletas] Empresa ${ruc}: ${todasSolicitudes.length} solicitudes desde ausencias/{dni}/ausencias, ${data.length} papeletas por tipo. Tiempo: ${Date.now() - startedAt} ms`
            );
            setSolicitudes(sortSolicitudes(data));
        } catch (error) {
            console.error("Error cargando papeletas:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestApproval = async (solicitud) => {
        if (!policyLevels.length) {
            await Swal.fire("Política requerida", "No existe política de aprobación para Control de Papeletas.", "warning");
            return;
        }
        const pending = getRequestBySolicitud(solicitud);
        const levels = getRequestLevels(pending);
        const sourceLevels = levels.length ? levels : policyLevels.map((lvl) => ({ level: lvl.level, role: (lvl.roles || [])[0] || "" }));
        const modalLevels = sourceLevels.map((lvl) => ({
            role: toRoleLabel(lvl.role),
            level: `L${lvl.level}`,
        }));
        const result = await requestApprovalWithReasonModal({
            title: "Solicitar aprobación de papeleta",
            actionLabel: "la aprobación de papeleta",
            inputLabel: "Motivo de solicitud",
            inputPlaceholder: "Explique por qué corresponde aprobar esta papeleta...",
            confirmButtonText: "Enviar solicitud",
            levels: modalLevels,
        });
        if (!result.isConfirmed) return;

        try {
            setProcessingRequestId(solicitud.id);
            const tenantId = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
            const callable = httpsCallable(functions, "submitApprovalRequest");
            await callable({
                tenantId,
                processType: activeProcessType,
                resourceType: "ausencias_dni_ausencias",
                resourceId: solicitud._path || solicitud.id,
                reason: String(result.value || "").trim(),
                payloadSnapshot: {
                    solicitudId: solicitud.id,
                    path: solicitud._path || "",
                    empleadoDni: solicitud.empleadoDni || "",
                    nombreEmpleado: solicitud.nombreEmpleado || "",
                    tipo: solicitud.tipo || "",
                },
            });
            await showApprovalRequestCreatedAlert({
                pendingSubject: "La solicitud de aprobación de papeleta",
                levels: modalLevels.map((lvl) => `${lvl.role} (${lvl.level})`),
            });
            await cargarSolicitudesAprobacion();
        } catch (error) {
            console.error("Error al solicitar aprobación de papeleta:", error);
            await Swal.fire("Error", error?.message || "No se pudo registrar la solicitud.", "error");
        } finally {
            setProcessingRequestId("");
        }
    };

    const aprobarDirectoSolicitud = async (solicitud) => {
        if (!solicitud?._path) return;
        const patch = {
            estado: "aprobado",
            aprobado: true,
            approvalStatus: "approved",
            aprobadoPor: usuario?.email || "",
        };
        await updateDoc(doc(db, solicitud._path), {
            ...patch,
            fechaAprobacion: serverTimestamp(),
            updated_at: serverTimestamp(),
        });
        await applyPapeletaToRolTurnos(solicitud);
        upsertSolicitudLocal(solicitud._path, patch);
    };

    const handleDecision = async (solicitud, requestId, decision) => {
        const result = await Swal.fire({
            title: decision === "approve" ? "Aprobar solicitud" : "Rechazar solicitud",
            input: "textarea",
            inputLabel: "Comentario",
            inputPlaceholder: "Comentario opcional...",
            showCancelButton: true,
            confirmButtonText: decision === "approve" ? "Confirmar aprobación" : "Confirmar rechazo",
            cancelButtonText: "Cancelar",
        });
        if (!result.isConfirmed) return;

        try {
            setProcessingRequestId(solicitud.id);
            const tenantId = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
            const callable = httpsCallable(functions, "decideApprovalRequest");
            const response = await callable({
                tenantId,
                requestId,
                decision,
                comment: String(result.value || "").trim(),
            });

            const status = String(response?.data?.status || "");
            if (status === "approved" && solicitud?._path) {
                const patch = {
                    estado: "aprobado",
                    aprobado: true,
                    approvalStatus: "approved",
                };
                await updateDoc(doc(db, solicitud._path), {
                    ...patch,
                    updated_at: serverTimestamp(),
                });
                await applyPapeletaToRolTurnos({ ...solicitud, ...patch });
                upsertSolicitudLocal(solicitud._path, patch);
            }
            if (status === "rejected" && solicitud?._path) {
                const patch = {
                    estado: "rechazado",
                    aprobado: false,
                    approvalStatus: "rejected",
                };
                await updateDoc(doc(db, solicitud._path), {
                    ...patch,
                    updated_at: serverTimestamp(),
                });
                upsertSolicitudLocal(solicitud._path, patch);
            }

            const levelMatch = status.match(/^pending_l(\d+)$/i);
            const msg = levelMatch
                ? `Aprobación registrada. Pendiente aprobación de Nivel ${levelMatch[1]}.`
                : status === "approved"
                    ? "Solicitud aprobada."
                    : status === "rejected"
                        ? "Solicitud rechazada."
                        : "Operación completada.";
            await Swal.fire("Éxito", msg, "success");
            await cargarSolicitudesAprobacion();
        } catch (error) {
            console.error("Error al procesar aprobación de papeleta:", error);
            await Swal.fire("Error", error?.message || "No se pudo procesar la solicitud.", "error");
        } finally {
            setProcessingRequestId("");
        }
    };

    const solicitudesPendientes = solicitudes.filter(
        (sol) => normalizarEstadoSolicitud(sol.estado) === "pendiente"
    );

    const solicitudesFiltradas = solicitudesPendientes.filter(sol => {
        const matchesTexto = 
            sol.nombreEmpleado?.toLowerCase().includes(filtro.toLowerCase()) ||
            sol.empleadoDni?.includes(filtro);
        
        const fechaSol = sol.fechaInicio?.seconds 
            ? moment(sol.fechaInicio.seconds * 1000) 
            : moment(sol.fechaInicio);

        const tieneRangoCompleto = Boolean(fechaInicio && fechaFin);
        const matchesFecha = !tieneRangoCompleto || fechaSol.isBetween(fechaInicio, fechaFin, 'day', '[]');

        return matchesTexto && matchesFecha;
    });

    const toggleSelected = (id, checked) => {
        setSelectedIds((prev) =>
            checked ? Array.from(new Set([...prev, id])) : prev.filter((item) => item !== id)
        );
    };

    const toggleSelectAllVisible = (checked) => {
        if (!checked) {
            setSelectedIds([]);
            return;
        }
        const pendingIds = solicitudesFiltradas
            .filter((s) => normalizarEstadoSolicitud(s.estado) === "pendiente")
            .map((s) => s.id);
        setSelectedIds(pendingIds);
    };

    const aprobarMasivo = async () => {
        const selectedRows = solicitudesFiltradas.filter((s) => selectedIds.includes(s.id));
        if (selectedRows.length === 0) {
            await Swal.fire("Atención", "Selecciona al menos una papeleta.", "warning");
            return;
        }
        const confirm = await Swal.fire({
            title: "Aprobación masiva",
            text: `Se procesarán ${selectedRows.length} papeletas seleccionadas.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Procesar",
            cancelButtonText: "Cancelar",
        });
        if (!confirm.isConfirmed) return;

        let approved = 0;
        let skipped = 0;
        let errors = 0;
        const tenantId = String(usuario?.ruc || usuario?.rucEmpresa || "").trim();
        const decideCallable = httpsCallable(functions, "decideApprovalRequest");

        for (const sol of selectedRows) {
            try {
                const pendingRequest = getRequestBySolicitud(sol);
                const pendingLevel = getPendingLevelNumber(pendingRequest);
                const requestLevels = getRequestLevels(pendingRequest);
                const currentLevelConfig = requestLevels.find((lvl) => lvl.level === pendingLevel);
                const currentLevelRoles = Array.isArray(currentLevelConfig?.roles)
                    ? currentLevelConfig.roles
                    : [currentLevelConfig?.role];
                const canApproveCurrentLevel =
                    Boolean(pendingRequest) &&
                    Number.isFinite(pendingLevel) &&
                    currentLevelRoles
                        .map((role) => String(role || "").trim().toLowerCase())
                        .includes(rolActual);

                if (canCurrentUserActAsSingleApprover && !pendingRequest) {
                    await aprobarDirectoSolicitud(sol);
                    approved += 1;
                    continue;
                }

                if (canApproveCurrentLevel) {
                    const response = await decideCallable({
                        tenantId,
                        requestId: pendingRequest.id,
                        decision: "approve",
                        comment: `Aprobación masiva por ${usuario?.email || "usuario"}`,
                    });
                    const status = String(response?.data?.status || "");
                    if (status === "approved") {
                        await aprobarDirectoSolicitud(sol);
                    }
                    approved += 1;
                } else {
                    skipped += 1;
                }
            } catch (error) {
                console.error("Error en aprobación masiva de papeletas:", error);
                errors += 1;
            }
        }

        await Swal.fire(
            "Resultado",
            `Aprobadas: ${approved}. Omitidas: ${skipped}. Errores: ${errors}.`,
            errors > 0 ? "warning" : "success"
        );
        setSelectedIds([]);
        await cargarSolicitudesAprobacion();
    };

    const exportarExcel = () => {
        const reportData = solicitudesFiltradas.map(sol => ({
            "DNI": sol.empleadoDni,
            "Colaborador": sol.nombreEmpleado,
            "Tipo": TIPO_LABEL,
            "Fecha Inicio": moment(sol.fechaInicio?.seconds ? sol.fechaInicio.seconds * 1000 : sol.fechaInicio).format("DD/MM/YYYY"),
            "Fecha Fin": moment(sol.fechaFin?.seconds ? sol.fechaFin.seconds * 1000 : sol.fechaFin).format("DD/MM/YYYY"),
            "Días": sol.totalDias || 0,
            "Estado Solicitud": sol.estado?.toUpperCase()
        }));

        const ws = XLSX.utils.json_to_sheet(reportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Control Papeletas");
        XLSX.writeFile(wb, `Reporte_Papeletas_${moment().format("YYYYMMDD")}.xlsx`);
    };

    const stats = {
        total: solicitudesFiltradas.length,
        aprobadas: 0,
        pendientes: solicitudesFiltradas.length,
        observadas: 0
    };

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaSignature className="me-3" />
                                Control de Papeletas de Descanso
                            </h2>
                            <p className="mb-0 opacity-75">
                                Monitoreo masivo de firmas y descansos por régimen
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        <div className="d-flex justify-content-end mb-4">
                            <Button
                                variant="primary"
                                onClick={aprobarMasivo}
                                disabled={selectedIds.length === 0}
                                className="shadow-sm d-flex align-items-center gap-2 me-2"
                            >
                                Aprobar en masa
                            </Button>
                            <Button 
                                variant="success" 
                                onClick={exportarExcel} 
                                disabled={solicitudesFiltradas.length === 0}
                                className="shadow-sm d-flex align-items-center gap-2"
                            >
                                <FaFileExcel /> Exportar a Excel
                            </Button>
                        </div>

                        {/* KPIs */}
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="shadow-sm border-0 text-white bg-primary h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Total Papeletas</h6>
                                            <h2 className="fw-bold mb-0">{stats.total}</h2>
                                        </div>
                                        <FaCalendarAlt size={40} className="opacity-50" />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm border-0 text-white bg-success h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75">Aprobadas</h6>
                                            <h2 className="fw-bold mb-0">{stats.aprobadas}</h2>
                                            <small className="opacity-75">{stats.total > 0 ? ((stats.aprobadas / stats.total) * 100).toFixed(1) : 0}% del total</small>
                                        </div>
                                        <FaUserCheck size={40} className="opacity-50" />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm border-0 text-white bg-warning h-100">
                                    <Card.Body className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h6 className="opacity-75 text-dark">Pendientes de Aprobación</h6>
                                            <h2 className="fw-bold mb-0 text-dark">{stats.pendientes}</h2>
                                            <small className="opacity-75 text-dark">En revisión</small>
                                        </div>
                                        <FaClock size={40} className="opacity-50 text-dark" />
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Filtros */}
                        <Card className="shadow-sm border-0 mb-4">
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={5}>
                                        <Form.Label className="small fw-bold">Buscar Trabajador</Form.Label>
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-white border-end-0">
                                                <FaSearch className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Nombre o DNI..."
                                                className="border-start-0"
                                                value={filtro}
                                                onChange={(e) => setFiltro(e.target.value)}
                                            />
                                        </InputGroup>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Label className="small fw-bold">Fecha Desde</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="date"
                                            value={fechaInicio}
                                            onChange={(e) => setFechaInicio(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Label className="small fw-bold">Fecha Hasta</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="date"
                                            value={fechaFin}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label className="small fw-bold">Estado Solicitud</Form.Label>
                                        <Form.Control size="sm" value="Pendientes" disabled />
                                    </Col>
                                </Row>
                                {policyLoaded && policyLevels.length === 0 && (
                                    <div className="mt-3 text-danger small fw-semibold">
                                        No hay política activa para Control de Papeletas. Configúrala en Niveles de Aprobación.
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Tabla */}
                        <Card className="shadow-sm border-0">
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Cargando registros...</p>
                                    </div>
                                ) : (
                                    <Table responsive hover className="mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: 40 }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={
                                                            solicitudesFiltradas.length > 0 &&
                                                            solicitudesFiltradas
                                                                .filter((s) => normalizarEstadoSolicitud(s.estado) === "pendiente")
                                                                .every((s) => selectedIds.includes(s.id))
                                                        }
                                                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                                                    />
                                                </th>
                                                <th>Colaborador</th>
                                                <th>DNI</th>
                                                <th>Periodo</th>
                                                <th>Días</th>
                                                <th>Estado Aprobación</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {solicitudesFiltradas.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center py-4 text-muted">
                                                        No se encontraron papeletas en este rango.
                                                    </td>
                                                </tr>
                                            ) : (
                                                solicitudesFiltradas.map((sol) => {
                                                    const pendingRequest = getRequestBySolicitud(sol);
                                                    const pendingLevel = getPendingLevelNumber(pendingRequest);
                                                    const requestLevels = getRequestLevels(pendingRequest);
                                                    const currentLevelConfig = requestLevels.find(
                                                        (lvl) => lvl.level === pendingLevel
                                                    );
                                                    const currentLevelRoles = Array.isArray(currentLevelConfig?.roles)
                                                        ? currentLevelConfig.roles
                                                        : [currentLevelConfig?.role];
                                                    const canApproveCurrentLevel =
                                                        Boolean(pendingRequest) &&
                                                        Number.isFinite(pendingLevel) &&
                                                        currentLevelRoles
                                                            .map((role) => String(role || "").trim().toLowerCase())
                                                            .includes(String(usuario?.rol || "").trim().toLowerCase());
                                                    return (
                                                        <tr key={sol.id}>
                                                            <td>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={selectedIds.includes(sol.id)}
                                                                    disabled={normalizarEstadoSolicitud(sol.estado) !== "pendiente"}
                                                                    onChange={(e) => toggleSelected(sol.id, e.target.checked)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="fw-bold">{sol.nombreEmpleado}</div>
                                                            </td>
                                                            <td className="text-muted">{sol.empleadoDni}</td>
                                                            <td>
                                                                <div>{moment(sol.fechaInicio?.seconds ? sol.fechaInicio.seconds * 1000 : sol.fechaInicio).format("DD/MM/YYYY")}</div>
                                                                <small className="text-muted">al {moment(sol.fechaFin?.seconds ? sol.fechaFin.seconds * 1000 : sol.fechaFin).format("DD/MM/YYYY")}</small>
                                                            </td>
                                                            <td>
                                                                <Badge bg="info">{sol.totalDias || 0} días</Badge>
                                                            </td>
                                                            <td>
                                                                <Badge bg={sol.estado === "aprobado" ? "success" : sol.estado === "pendiente" ? "warning" : "danger"} className="text-uppercase me-2">
                                                                    {sol.estado || "pendiente"}
                                                                </Badge>
                                                                {pendingRequest && (
                                                                    <Badge bg={pendingLevel === 1 ? "warning" : "info"} className="text-uppercase">
                                                                        {Number.isFinite(pendingLevel)
                                                                            ? `Esperando ${toRoleLabel(currentLevelConfig?.role)} (L${pendingLevel})`
                                                                            : "Pendiente de aprobación"}
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {sol.pdfUrl && (
                                                                    <Button 
                                                                        variant="link" 
                                                                        size="sm"
                                                                        href={sol.pdfUrl} 
                                                                        target="_blank"
                                                                    >
                                                                        Ver PDF
                                                                    </Button>
                                                                )}
                                                                {!pendingRequest && normalizarEstadoSolicitud(sol.estado) === "pendiente" && !canCurrentUserActAsSingleApprover && (
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        className="ms-2"
                                                                        onClick={() => handleRequestApproval(sol)}
                                                                        disabled={processingRequestId === sol.id}
                                                                    >
                                                                        Solicitar aprobación
                                                                    </Button>
                                                                )}
                                                                {!pendingRequest && normalizarEstadoSolicitud(sol.estado) === "pendiente" && canCurrentUserActAsSingleApprover && (
                                                                    <Button
                                                                        variant="outline-success"
                                                                        size="sm"
                                                                        className="ms-2"
                                                                        disabled={processingRequestId === sol.id}
                                                                        onClick={async () => {
                                                                            try {
                                                                                setProcessingRequestId(sol.id);
                                                                                await aprobarDirectoSolicitud(sol);
                                                                                await Swal.fire("Éxito", "Solicitud aprobada.", "success");
                                                                            } catch (error) {
                                                                                console.error("Error aprobando papeleta:", error);
                                                                                await Swal.fire("Error", "No se pudo aprobar la papeleta.", "error");
                                                                            } finally {
                                                                                setProcessingRequestId("");
                                                                            }
                                                                        }}
                                                                    >
                                                                        Aprobar
                                                                    </Button>
                                                                )}
                                                                {canApproveCurrentLevel && (
                                                                    <>
                                                                        <Button
                                                                            variant="outline-success"
                                                                            size="sm"
                                                                            className="ms-2"
                                                                            disabled={processingRequestId === sol.id}
                                                                            onClick={() => handleDecision(sol, pendingRequest.id, "approve")}
                                                                        >
                                                                            Aprobar
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            className="ms-2"
                                                                            disabled={processingRequestId === sol.id}
                                                                            onClick={() => handleDecision(sol, pendingRequest.id, "reject")}
                                                                        >
                                                                            Rechazar
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>

                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default ControlPapeletas;
